import React, { useState } from "react";
import axios from "axios";
import {
  TextField,
  Button,
  Box,
  Snackbar,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import useSharedLink from "../SharedLink/index";
import { xorDecrypt } from "../encrypt";
import axiosInstance from "../api/axiosInstance";

const AddClient = ({ handleClose, refreshClientList }) => {
  const subscriberId = xorDecrypt(localStorage.getItem("subscriberId"));

  const [clientData, setClientData] = useState({
    subscriber: {
      id: subscriberId,
    },
    clientName: "",
    dateOfBirth: "",
    profileDetails: "",
    sessionNotes: [],
  });

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [dialogOpen, setDialogOpen] = useState(false);

  const sharedLink = useSharedLink();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setClientData({ ...clientData, [name]: value });
  };

  const handleAddClient = async (e) => {
    e.preventDefault();
    setSnackbarMessage("Please wait...");
    setSnackbarSeverity("info");
    setSnackbarOpen(true);
    
    try {
      const response = await axiosInstance.post(
        `${sharedLink}/api/abanotes/addclient`,
        clientData,
        { withCredentials: true }
      );
  
      if (response.status === 200) {
        // Show success message first
        setSnackbarMessage("Client created successfully!");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        
        // Reset form data
        setClientData({
          subscriber: {
            id: subscriberId,
          },
          clientName: "",
          dateOfBirth: "",
          profileDetails: "",
          sessionNotes: [],
        });
        
        // Add a small delay before refreshing to ensure server has processed
        setTimeout(() => {
          // Call the refreshClientList to update the grid
          if (refreshClientList && typeof refreshClientList === "function") {
            console.log("Refreshing client list...");
            refreshClientList();
          }
          
          // Close dialog after a delay
          setTimeout(() => {
            handleClose();
          }, 1000);
        }, 500);
      }
    } catch (error) {
      // Error handling remains the same
      setSnackbarSeverity("error");
      if (error.response) {
        setSnackbarMessage(
          `Server error: ${error.response.status}. Please try again later.`
        );
      } else if (error.request) {
        setSnackbarMessage(
          "No response from the server. Please check your network connection."
        );
      } else {
        setSnackbarMessage("An error occurred. Please try again.");
      }
      setSnackbarOpen(true);
    }
  };

  const handleCancel = () => {
    setDialogOpen(true);
  };

  const handleDialogClose = (confirm) => {
    setDialogOpen(false);
    if (confirm) {
      handleClose();
    }
  };

  const today = new Date().toISOString().split("T")[0];

  return (
    <Box
      component="form"
      onSubmit={handleAddClient}
      sx={{
        display: "flex",
        flexDirection: "column",
        padding: 3,
        backgroundColor: "#000",
        boxShadow: "0px 0px 15px 5px rgba(255, 255, 255, 0.2)",
        borderRadius: "10px",
        width: "400px",
        margin: "0 auto",
        boxSizing: "border-box",
      }}
    >
      <h2 style={{ marginBottom: "20px", color: "#fff" }}>Add Client:</h2>

      <TextField
        required
        type="text"
        name="clientName"
        label="Client Name"
        value={clientData.clientName}
        onChange={handleChange}
        inputProps={{
          maxLength: 20, // Limit to 20 characters
        }}
        sx={{
          marginBottom: 2,
          "& .MuiInputLabel-root": { color: "#e9a132" },
          "& .MuiInputBase-root": {
            color: "white",
            backgroundColor: "#333",
          },
          "& .MuiOutlinedInput-root": {
            "& fieldset": {
              borderColor: "#e9a132",
            },
            "&:hover fieldset": {
              borderColor: "#d0892f",
            },
            "&.Mui-focused fieldset": {
              borderColor: "#d0892f",
            },
          },
        }}
        InputLabelProps={{ shrink: true }}
      />

      <TextField
        required
        type="date"
        name="dateOfBirth"
        label="Date of Birth"
        value={clientData.dateOfBirth}
        onChange={handleChange}
        inputProps={{
          max: today, // Set the max attribute to today's date
        }}
        sx={{
          marginBottom: 2,
          "& .MuiInputLabel-root": { color: "#e9a132" },
          "& .MuiInputBase-root": {
            color: "white",
            backgroundColor: "#333",
          },
          "& .MuiOutlinedInput-root": {
            "& fieldset": {
              borderColor: "#e9a132",
            },
            "&:hover fieldset": {
              borderColor: "#d0892f",
            },
            "&.Mui-focused fieldset": {
              borderColor: "#d0892f",
            },
          },
        }}
        InputLabelProps={{ shrink: true }}
      />

      <TextField
        required
        type="text"
        name="profileDetails"
        label="Profile Details"
        value={clientData.profileDetails}
        onChange={handleChange}
        inputProps={{ maxLength: 200 }}
        sx={{
          marginBottom: 2,
          "& .MuiInputLabel-root": { color: "#e9a132" },
          "& .MuiInputBase-root": {
            color: "white",
            backgroundColor: "#333",
          },
          "& .MuiOutlinedInput-root": {
            "& fieldset": {
              borderColor: "#e9a132",
            },
            "&:hover fieldset": {
              borderColor: "#d0892f",
            },
            "&.Mui-focused fieldset": {
              borderColor: "#d0892f",
            },
          },
        }}
        InputLabelProps={{ shrink: true }}
      />

      <Button
        type="submit"
        variant="contained"
        color="primary"
        sx={{
          marginTop: 2,
          backgroundColor: "#e9a132",
          "&:hover": { backgroundColor: "#d0892f" },
          color: "white",
        }}
      >
        Add Client
      </Button>

      <Button
        type="button"
        variant="contained"
        onClick={handleCancel}
        sx={{
          marginTop: 2,
          backgroundColor: "red",
          "&:hover": { backgroundColor: "darkred" },
          color: "white",
        }}
      >
        Cancel
      </Button>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <Dialog
        open={dialogOpen}
        onClose={() => handleDialogClose(false)}
        PaperProps={{
          sx: {
            backgroundColor: "#333",
            color: "white",
          },
        }}
      >
        <DialogTitle>{"Confirm Cancel"}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: "white" }}>
            Are you sure you want to cancel? Any unsaved changes will be lost.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => handleDialogClose(false)}
            sx={{
              color: "white",
              "&:hover": { backgroundColor: "#444" },
            }}
          >
            No
          </Button>
          <Button
            onClick={() => handleDialogClose(true)}
            sx={{
              color: "white",
              backgroundColor: "#e9a132",
              "&:hover": { backgroundColor: "#d0892f" },
            }}
            autoFocus
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AddClient;
